// ─── Transient error classification ──────────────────────────────────────────
// Shared by the axios response interceptor (which tags errors with these codes)
// and the react-query client (which decides what is worth retrying). Kept in its
// own module so importing the predicate doesn't drag in `client.ts` and, with it,
// the side effect of constructing the axios instance and registering interceptors.
//
// The distinction that matters here is "the server answered" vs "it didn't".
// Anything with a status code is a decision the backend made and must be shown to
// the user as-is; only a silent request — no response at all — is worth retrying.

import type { ApiError } from '../types';

/** The request left the browser but no response came back (connection dropped, CORS-blocked 502, origin restarting). */
export const NETWORK_ERROR_CODE = 'NETWORK_ERROR';

/** The request was still outstanding when the axios `timeout` elapsed — a slow server, not a bad connection. */
export const TIMEOUT_ERROR_CODE = 'TIMEOUT_ERROR';

const TRANSIENT_CODES: ReadonlySet<string> = new Set([NETWORK_ERROR_CODE, TIMEOUT_ERROR_CODE]);

// Both codes are set by the interceptor alongside `statusCode: 0`. We key off the
// code rather than the status so a future backend that genuinely returns 0 for
// something else can't accidentally opt itself into retries.
export function isTransientApiError(err: unknown): boolean {
  const code = (err as ApiError | null)?.code;
  return typeof code === 'string' && TRANSIENT_CODES.has(code);
}
